import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/currency';
import {
  CreditCardIcon,
  UsersIcon,
  BuildingOfficeIcon,
  TruckIcon,
  CubeIcon
} from '@heroicons/react/24/outline';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const defaultStats = {
  expenses: {
    total: 0,
    pending: { count: 0, amount: 0 },
    approved: { count: 0, amount: 0 },
    rejected: { count: 0, amount: 0 },
    paid: { count: 0, amount: 0 },
    totalAmount: 0,
    byType: {}
  },
  office: {
    total: 0,
    pending: { count: 0, amount: 0 },
    approved: { count: 0, amount: 0 },
    rejected: { count: 0, amount: 0 },
    paid: { count: 0, amount: 0 },
    totalAmount: 0
  },
  users: { total: 0, active: 0 },
  clients: { total: 0, totalDues: 0 },
  cars: { total: 0, totalExpenses: 0 },
  assets: { total: 0, active: 0, currentValue: 0 }
};

const Dashboard = () => {
  const { user, canAccess } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(defaultStats);
  const [recentExpenses, setRecentExpenses] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/reports/summary');
      const data = response.data;

      setStats({
        expenses: data.expenses || defaultStats.expenses,
        office: data.office || defaultStats.office,
        users: data.users || defaultStats.users,
        clients: data.clients || defaultStats.clients,
        cars: data.cars || defaultStats.cars,
        assets: data.assets || defaultStats.assets
      });
      setRecentExpenses(data.recentExpenses || []);
      setMonthlyData(data.monthlyOverview || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
    <div className="card p-6">
      <div className="flex items-center">
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-semibold text-gray-900">{value}</p>
          {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
        </div>
      </div>
    </div>
  );

  const getStatusBadge = (status) => {
    const classes = {
      pending: 'status-pending',
      approved: 'status-approved',
      rejected: 'status-rejected',
      paid: 'status-paid'
    };
    return classes[status] || 'status-pending';
  };

  const typeLabel = (type) => {
    if (type === 'indent' || type === 'intend') return 'Indent';
    return type ? type.charAt(0).toUpperCase() + type.slice(1) : 'N/A';
  };

  const statusData = [
    { name: 'Pending', value: stats.expenses.pending?.count || 0, color: '#f59e0b' },
    { name: 'Approved', value: stats.expenses.approved?.count || 0, color: '#10b981' },
    { name: 'Paid', value: stats.expenses.paid?.count || 0, color: '#3b82f6' },
    { name: 'Rejected', value: stats.expenses.rejected?.count || 0, color: '#ef4444' }
  ];
  const pieData = statusData.filter((entry) => entry.value > 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">Welcome back, {user?.name}</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Expenses"
          value={stats.expenses.total}
          icon={CreditCardIcon}
          color="bg-blue-500"
          subtitle={`${formatCurrency(stats.expenses.totalAmount)} total value`}
        />

        <StatCard
          title="Pending Approvals"
          value={stats.expenses.pending?.count || 0}
          icon={CreditCardIcon}
          color="bg-yellow-500"
          subtitle={`${formatCurrency((stats.expenses.pending?.amount || 0) + (stats.office.pending?.amount || 0))} pending`}
        />

        {canAccess('office') && (
          <StatCard
            title="Office Spend"
            value={formatCurrency(stats.office.totalAmount)}
            icon={BuildingOfficeIcon}
            color="bg-red-500"
            subtitle={`${stats.office.pending?.count || 0} pending bills`}
          />
        )}
        
        {canAccess('users') && (
          <StatCard
            title="Active Users"
            value={stats.users.active}
            icon={UsersIcon}
            color="bg-green-500"
            subtitle={`${stats.users.total} total`}
          />
        )}
        
        {canAccess('clients') && (
          <StatCard
            title="Client Dues"
            value={formatCurrency(stats.clients.totalDues)}
            icon={BuildingOfficeIcon}
            color="bg-yellow-500"
            subtitle={`${stats.clients.total} clients`}
          />
        )}
        
        {canAccess('cars') && (
          <StatCard
            title="Car Expenses"
            value={formatCurrency(stats.cars.totalExpenses)}
            icon={TruckIcon}
            color="bg-purple-500"
            subtitle={`${stats.cars.total} vehicles`}
          />
        )}

        {canAccess('assets') && (
          <StatCard
            title="Asset Value"
            value={formatCurrency(stats.assets.currentValue)}
            icon={CubeIcon}
            color="bg-indigo-500"
            subtitle={`${stats.assets.total} assets`}
          />
        )}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Expenses Chart */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Overview</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip formatter={(value) => [formatCurrency(value), '']} />
              <Bar dataKey="expenses" fill="#3b82f6" name="Spending" />
              <Bar dataKey="income" fill="#10b981" name="Client Payments" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Expense Status Distribution */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Expense Status</h3>
          <div className="grid grid-cols-3 gap-4 items-center">
            <div className="col-span-2">
              {pieData.length === 0 ? (
                <div className="flex items-center justify-center h-[260px] text-sm text-gray-500">
                  No expense records yet
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${value}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
            <ul className="space-y-2 text-sm">
              {statusData.map((entry) => (
                <li key={entry.name} className="flex items-center justify-between">
                  <span className="flex items-center">
                    <span
                      className="inline-block h-2.5 w-2.5 rounded-full mr-2"
                      style={{ backgroundColor: entry.color }}
                    />
                    <span className="text-gray-700">{entry.name}</span>
                  </span>
                  <span className={`font-medium ${entry.value === 0 ? 'text-gray-400' : 'text-gray-900'}`}>
                    {entry.value}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Recent Expenses */}
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Recent Expenses</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Purpose
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recentExpenses.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                    No expenses found
                  </td>
                </tr>
              ) : (
                recentExpenses.map((expense) => (
                  <tr key={expense._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="capitalize font-medium text-gray-900">
                        {typeLabel(expense.type)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{expense.purpose}</div>
                      <div className="text-sm text-gray-500 truncate max-w-xs">
                        {expense.description}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(expense.amount.approved || expense.amount.requested)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={getStatusBadge(expense.status)}>
                        {expense.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(expense.date).toLocaleDateString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button onClick={() => navigate('/expenses')} className="btn-primary text-sm">
            New Expense
          </button>
          {canAccess('users') && (
            <button onClick={() => navigate('/users')} className="btn-secondary text-sm">
              Add User
            </button>
          )}
          {canAccess('clients') && (
            <button onClick={() => navigate('/clients')} className="btn-secondary text-sm">
              Add Client
            </button>
          )}
          {canAccess('assets') && (
            <button onClick={() => navigate('/assets')} className="btn-secondary text-sm">
              Add Asset
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
